import _ from "lodash";
import React, { useEffect, useState, useContext } from "react";
import { Button, Col, Dropdown, DropdownItem, DropdownMenu, Row, Table } from "reactstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DebounceInput from "react-debounce-input";
import { toast } from "react-toastify";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers, UserContext } from "../utils";
import { CustomerTypeahead, ItemTypeahead, Pager, ServiceThreadSpinner, SortHeader, SpinnerButton, VendorTypeahead } from "./";

const defaultSelect = { label: "--- Select ---", value: 0 };
const {
   CORRECTIVE_ACTION_STATUSES_SELECT,
   CORRECTIVE_ACTION_TYPES_NEW, // the only difference is the first option
   CORRECTIVE_ACTION_TYPES_SEARCH, // this one begins with --- All ---
} = constants;
const { ROLE_IDS } = constants;
const adminPowerQual = [ROLE_IDS.Admin, ROLE_IDS.QualityEdit, ROLE_IDS.PowerUser];

const today = new Date();
const thisMonth = today.getMonth() + 1;
const thisDay = today.getDate();
const thisYear = today.getFullYear();
const TodaysDate = `${thisMonth}/${thisDay}/${thisYear}`;

export default function CorrectiveActionSearch(props) {
   const cancelToken = axios.CancelToken;
   const navigate = useNavigate();
   const source = cancelToken.source();
   const userCtx = useContext(UserContext);
   const { currentUser } = useContext(UserContext);
   const roles = !_.isNil(currentUser) ? _.map(currentUser.roles, (r) => r.typeOfUserRole) : [];
   const EDITING_ALLOWED = !_.chain(roles).intersectionWith(adminPowerQual, _.isEqual).isEmpty().value();

   const [isLoading, setIsLoading] = useState(true);
   const [download, setDownload] = useState(false);      // Show or hide spinner on spinner buttons
   const [list, setList] = useState({
      data: [],
      TotalPages: 0
   });
   const [screenForm, setScreenForm] = useState({
      payload: {
         actionType: { label: "--- All ---", value: 0 },
         actionStatus: { label: "--- All ---", value: 0 },
         problemType: { label: "--- All ---", value: 0 },
         customerNumber: defaultSelect,
         vendorNumber: defaultSelect,
         itemNumber: defaultSelect,
         startDate: "",
         endDate: ""
      },
      pageController: {
         pageNumber: 1,
         pageSize: 25
      },
      sortController: {
         sortDirection: "desc",
         sortField: "CreatedDate"
      }
   });
   const [selectList, setSelectList] = useState({
      NewItemList: [],
      ActionTypeList: [],
      StatusTypeList: [],
      ProblemTypeList: [],
   });

   useEffect(() => {
      document.title = `Corrective Action`
      api
         .fetchS("Quality/LoadCorrectActionSearchSelectLists", {}, source.token)
         .then((res) => {
            if (res.data?.success) {
               if (!res.data.correctiveActionLists?.length)
                  return toast.error("list came back empty");
               let CORRECT_ACTION_PROBLEM_LIST = res.data.correctiveActionLists;
               setSelectList({
                  NewItemList: CORRECTIVE_ACTION_TYPES_NEW,
                  ActionTypeList: CORRECTIVE_ACTION_TYPES_SEARCH,
                  StatusTypeList: CORRECTIVE_ACTION_STATUSES_SELECT,
                  ProblemTypeList: CORRECT_ACTION_PROBLEM_LIST,
               });
            } else {
               throw new Error("failed to fetch list");
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            return null;
         });

      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      refreshData(); // new page a filter 
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [screenForm])

   const createPayload = (copy = null) => {
      const payloadOnState = copy === null ? _.cloneDeep(screenForm) : copy;
      const payload = Object.assign({}, payloadOnState.payload, payloadOnState.pageController, payloadOnState.sortController);
      payload.customerId = helpers.valueFromSelect(payload.customerNumber, true);
      payload.vendorId = helpers.valueFromSelect(payload.vendorNumber, true);
      payload.itemNumber = helpers.valueFromSelect(payload.itemNumber, true);
      payload.actionType = helpers.valueFromSelect(payload.actionType, false, true);
      payload.actionStatus = helpers.valueFromSelect(payload.actionStatus, false);
      payload.problemType = helpers.valueFromSelect(payload.problemType, false);
      payload.isICARProblemType = payload.problemType > 5000;
      payload.startDate = dateHelpers.formatDateForServer(payload.startDate);
      payload.endDate = dateHelpers.formatDateForServer(payload.endDate);
      return payload;
   }

   const createRequest = () => {
      const copy = _.cloneDeep(screenForm);
      let actionType = -1;
      if (copy.payload.newItem && copy.payload.newItem.value) actionType = copy.payload.newItem.value;
      if (actionType === -1) {
         toast.error("Invalid Action! cannot create new request")
         return;
      }
      switch (actionType) {
         case constants.CORRECTIVE_ACTION_TYPES.InternalAction:
            api.postS("Quality/SaveCorrectiveAction", { actionId: 0, actionType: constants.CORRECTIVE_ACTION_TYPES.InternalAction }, source.token).then(
               r => {
                  if (r.data.success) {
                     let newActionId = r.data.actionId;
                     if (typeof (newActionId) === "string") parseInt(newActionId);
                     navigate(`${constants.ENDPOINTS.correctiveInternalActionEdit}/${newActionId}`);
                  }
                  else {
                     toast.error(r.data.message);
                  }
               }
            ).catch(api.catchHandler);
            break;
         case constants.CORRECTIVE_ACTION_TYPES.CorrectiveAction:
            api.postS("Quality/SaveCorrectiveAction", { actionId: 0, actionType: constants.CORRECTIVE_ACTION_TYPES.CorrectiveAction }, source.token).then(
               r => {
                  if (r.data.success) {
                     let newActionId = r.data.actionId;
                     if (typeof (newActionId) === "string") parseInt(newActionId);
                     navigate(`${constants.ENDPOINTS.correctiveActionEdit}/${newActionId}`);
                  }
                  else {
                     toast.error(r.data.message);
                  }
               }
            ).catch(api.catchHandler);
            break;
         case constants.CORRECTIVE_ACTION_TYPES.SupplierAction:
            api.postS("Quality/SaveVendorAction", { actionId: 0 }, source.token).then(
               r => {
                  if (r.data.success) {
                     let newActionId = r.data.actionId;
                     if (typeof (newActionId) === "string") parseInt(newActionId);
                     navigate(`${constants.ENDPOINTS.correctiveActionSupplierEdit}/${newActionId}`);
                  }
                  else {
                     toast.error(r.data.message);
                  }
               }
            ).catch(api.catchHandler);
            break;
         case constants.CORRECTIVE_ACTION_TYPES.PreventativeAction:
            api.postS("Quality/SavePreventativeAction", { actionId: 0 }, source.token).then(
               r => {
                  if (r.data.success) {
                     let newActionId = r.data.actionId;
                     if (typeof (newActionId) === "string") parseInt(newActionId);
                     navigate(`${constants.ENDPOINTS.correctiveActionPreventativeEdit}/${newActionId}`);
                  }
                  else {
                     toast.error(r.data.message);
                  }
               }
            ).catch(api.catchHandler);
            break;
         default:
            toast.info("Contact your admin, a critical error has occured");
            break;
      }
   }

   const deleteCorrective = (actionType, actionId) => {
      const copy = _.cloneDeep(screenForm);
      _.set(copy, "payload.actionId", actionId);
      _.set(copy, "payload.actionType", { label: "NotFalsey", value: actionType })
      api.postS("Quality/DeleteCorrectiveAction", createPayload(copy), source.token)
         .then(r => {
            if (r.data.success) refreshData();
            else throw new Error(r.data?.message);
         })
         .catch((error) => {
            toast.error(error);
            api.catchHandler(error);
         })
   }

   function exportGrid() {
      setDownload(true);
      api
         .postGetBlob("Quality/ExportActions", createPayload())
         .then(response => {
            if (response && response.data) {
               if (response.data.success === false) {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response?.data?.message);
                  }
                  return;
               }
               if (response.data) {
                  const XLSX_FILENAME = `CorrectiveSearchActions.xlsx`
                  helpers.downloadExport(response.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
               }
            } else {
               toast.error(response.message || "There was an error downloading the file");
            }
         }).catch((error) => {
            api.catchHandler(error)
            toast.error("There was an error downloading the file");
         })
         .finally(() => setDownload(false));
   }

   const onChange = (property, value, optionalDirectionOnly = null) => {
      let copy = _.cloneDeep(screenForm);
      if (optionalDirectionOnly != null) _.set(copy, "sortController.sortDirection", optionalDirectionOnly);
      _.set(copy, property, value);
      _.set(copy, "pageController.pageNumber", 1);
      setScreenForm(copy);
      return;
   };

   const printReport = () => {
      setIsLoading(true);
      api.postDownloadBinary(
         'Report/CorrectiveActionSummary',
         createPayload(),
         constants.MIME_TYPES.pdf,
         "CAR_Summary_" + TodaysDate + ".pdf",
         "There was an error downloading this corrective action summary pdf"
      )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setIsLoading(false));
   };

   const refreshData = () => {
      setIsLoading(true);
      api.postS("Quality/GridData", createPayload(), source.token).then(r => {
         let newData = r.data && r.data.success ? r.data.paginatedList.items : [];
         setList({
            data: newData,
            TotalPages: r.data.paginatedList.totalCount
         });
         if (!r.data.success) {
            toast.error("Failed to fetch grid data")
            throw new Error(r.data.message)
         }
      }).catch(api.catchHandler)
         .finally(() => setIsLoading(false))
   }

   const viewReport = (actionId, actionType, displayId) => {
      if (!actionId || !actionType || !displayId) {
         toast.error("The action id or action type or display id was null");
         return;
      }
      //send the primary key action Id and the action Type to decide which report to retieve
      const copy = _.cloneDeep(screenForm);
      _.set(copy, "payload.actionType", { value: actionType, label: "notFalsey" });
      _.set(copy, "payload.actionId", actionId);
      setIsLoading(true);
      api.postDownloadBinary(
         'Report/CorrectiveAction',
         createPayload(copy),
         constants.MIME_TYPES.pdf,
         displayId + ".pdf",
         "There was an error downloading this corrective action pdf"
      )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setIsLoading(false));
   }

   return (
      <DesktopContainer screenName={"Corrective Action"}>
         <div className="filter-header stick-top mb-3">
            {EDITING_ALLOWED &&
               <>
                  <Row className="mt-3">
                     <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                        <h5>Create New Action Request Item:</h5>
                     </Col>
                     <Col xs="6">
                        <Select
                           classNamePrefix="serviceThreadSelect"
                           options={selectList.NewItemList}
                           value={screenForm.payload.newItem}
                           onChange={(opt) => onChange("payload.newItem", opt)}
                           isDisabled={isLoading}
                        />
                     </Col>
                     <Col>
                        <Button
                           className="shredButton"
                           onClick={createRequest}
                           disabled={screenForm.payload.newItem === (null || undefined) || screenForm.payload?.newItem.value === -1}
                        >
                           CREATE REQUEST
                        </Button>
                     </Col>
                  </Row>
                  <hr />
               </>
            }
            <Row>
               <Col xs="3" style={{ textAlign: "right", color: "white" }}>
                  <h5>Search</h5>
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  Action Type:
               </Col>
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={selectList.ActionTypeList}
                     value={screenForm.payload.actionType}
                     onChange={(opt) => onChange("payload.actionType", opt)}
                     isDisabled={isLoading}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  Status #:
               </Col>
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={selectList.StatusTypeList}
                     value={screenForm.payload.actionStatus}
                     onChange={(opt) => onChange("payload.actionStatus", opt)}
                     isDisabled={isLoading}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  Problem Type:
               </Col>
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={selectList.ProblemTypeList}
                     value={screenForm.payload.problemType}
                     onChange={(opt) => onChange("payload.problemType", opt)}
                     isDisabled={isLoading}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  Customer:
               </Col>
               <Col xs="6" style={{ paddingRight: "0px" }}>
                  <CustomerTypeahead
                     value={screenForm.payload.customerNumber ?? defaultSelect}
                     onChange={(opt) => onChange("payload.customerNumber", opt)}
                     disabled={isLoading}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  Vendor:
               </Col>
               <Col xs="6" style={{ paddingRight: "0px" }}>
                  <VendorTypeahead
                     value={screenForm.payload.vendorNumber ?? defaultSelect}
                     onChange={(opt) => onChange("payload.vendorNumber", opt)}
                     disabled={isLoading}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  Item #:
               </Col>
               <Col xs="6" style={{ paddingRight: "0px" }}>
                  <ItemTypeahead
                     value={screenForm.payload.itemNumber ?? defaultSelect}
                     onChange={(opt) => onChange("payload.itemNumber", opt)}
                     disabled={isLoading}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  Start Date:
               </Col>
               <Col xs="6">
                  <DebounceInput
                     type="date"
                     onChange={(e) => onChange("payload.startDate", e.target.value)}
                     value={screenForm.payload.startDate}
                     debounceTimeout={500}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  End Date:
               </Col>
               <Col xs="6">
                  <DebounceInput
                     type="date"
                     onChange={(e) => onChange("payload.endDate", e.target.value)}
                     value={screenForm.payload.endDate}
                     debounceTimeout={500}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" />
               <Col>
                  <Button className="shredButton" onClick={() => printReport()} disabled={isLoading} >
                     PRINT REPORT
                  </Button>
               </Col>
            </Row>
         </div>


         <Row className="align-items-center mt-5 mb-3">
            <Col className="transparent-background">
               <Pager
                  callBack={(newPageNumber) => setScreenForm({ ...screenForm, pageController: { pageNumber: newPageNumber, pageSize: screenForm.pageController.pageSize } })}
                  pageNumber={screenForm.pageController.pageNumber}
                  pageSize={screenForm.pageController.pageSize}
                  totalPages={_.ceil(list.TotalPages / screenForm.pageController.pageSize)}
                  // sizeCallback={(newPageSize) => setScreenForm({...screenForm, pageController: {pageSize: newPageSize, pageNumber: screenForm.pageController.pageNumber}})}
                  disabled={isLoading}
               />
            </Col>
            <Col>
               <SpinnerButton
                  title="Download as .xlsx"
                  className="shredButtonExcel float-end mx-1"
                  onClick={exportGrid}
                  on={download}
                  disabled={download}
               ><FontAwesomeIcon icon="file-excel" size="lg" />
               </SpinnerButton>
            </Col>
         </Row>
         <ServiceThreadSpinner on={isLoading}>
            {
               list.data?.length !== 0
                  ?
                  <Table size="sm" className="data-table" style={{ padding: "5px" }}>
                     <thead className="table-header">
                        <tr>
                           <th >
                              <SortHeader
                                 displayName="Id"
                                 field="DisplayId"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "DisplayId"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th >
                              <SortHeader
                                 displayName="Open Date"
                                 field="CreatedDate"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "CreatedDate"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th >
                              <SortHeader
                                 displayName="Customer / Vendor"
                                 field="CustomerVendorName"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "CustomerVendorName"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th >
                              <SortHeader
                                 displayName="Problem Type"
                                 field="ProblemTypeDescription"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "ProblemTypeDescription"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th style={{ width: "40%" }}>
                              <SortHeader
                                 displayName="Description"
                                 field="ShortDescription"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "ShortDescription"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th >
                              <SortHeader
                                 displayName="Status"
                                 field="ActionStatusDescription"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "ActionStatusDescription"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th >
                              <SortHeader
                                 displayName="Completed"
                                 field="CompletionDate"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "CompletionDate"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th >
                              <SortHeader
                                 displayName={<span>Days To<br />Complete</span>}
                                 field="DaysToComplete"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "DaysToComplete"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           {userCtx && userCtx.currentUser && userCtx.currentUser.isQualityAdmin &&
                              (<th></th>)
                           }
                        </tr>
                     </thead>
                     <tbody>
                        {list.data && list.data.length && _.map(list.data, (c, index) => {
                           let navigationRoute = "";
                           switch (c.actionType) {
                              case constants.CORRECTIVE_ACTION_TYPES.InternalAction:
                                 navigationRoute = `${constants.ENDPOINTS.correctiveInternalActionEdit}/${c.actionId}`;
                                 break;
                              case constants.CORRECTIVE_ACTION_TYPES.PreventativeAction:
                                 navigationRoute = `${constants.ENDPOINTS.correctiveActionPreventativeEdit}/${c.actionId}`;
                                 break;
                              case constants.CORRECTIVE_ACTION_TYPES.SupplierAction:
                                 navigationRoute = `${constants.ENDPOINTS.correctiveActionSupplierEdit}/${c.actionId}`;
                                 break;
                              case constants.CORRECTIVE_ACTION_TYPES.CorrectiveAction:
                                 navigationRoute = `${constants.ENDPOINTS.correctiveActionEdit}/${c.actionId}`;
                                 break;
                              default:
                                 navigationRoute = -1;
                                 break;
                           }

                           return (
                              <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                 <CorrectiveActionDropDownMenu
                                    displayId={c.displayId}
                                    actionId={c.actionId}
                                    actionType={c.actionType}
                                    viewReport={viewReport}
                                    navRoute={navigationRoute}
                                    attachmentCount={c.attachmentCount}
                                    attachmentType={c.attachmentType}
                                    qualityAdmin={EDITING_ALLOWED}
                                 // itemDescription={c.itemWithDescription} 
                                 />
                                 <td className="data-cell">{dateHelpers.toLongDateString(c.createdDate)}</td>
                                 <td className="data-cell">{c.customerVendorName}</td>
                                 <td className="data-cell">{c.problemTypeDescription}</td>
                                 <td className="data-cell">{c.shortDescription}</td>
                                 <td className="data-cell">{c.actionStatusDescription}</td>
                                 <td className="data-cell">{dateHelpers.toLongDateString(c.completionDate) === "01/01/0001" ? " " : dateHelpers.toLongDateString(c.completionDate)}</td>
                                 <td className="data-cell">{c.daysToComplete}</td>
                                 {userCtx && userCtx.currentUser && userCtx.currentUser.isQualityAdmin &&
                                    (<td className="data-cell boldedOrange" style={{ cursor: "pointer" }} onClick={() => deleteCorrective(c.actionType, c.actionId)}>
                                       Delete
                                    </td>)
                                 }
                              </tr>
                           )
                        }
                        )}
                     </tbody>
                  </Table>
                  :
                  <div>
                     <span style={{ display: "flex", justifyContent: "center" }}>
                        No Corrective Actions To Show
                     </span>
                  </div>
            }
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}

function CorrectiveActionDropDownMenu(props) {
   const [open, setOpen] = useState(false);
   const navigate = useNavigate();

   const goNavigate = (navRoute) => {
      if (navRoute === -1) { toast.info("coming soon..."); return; }
      navigate(navRoute);
   }

   return (
      <td
         className="data-cell"
         onMouseEnter={() => setOpen(true)}
         onMouseLeave={() => setOpen(false)}
         style={{ position: "relative" }}
      >
         <span className="shredLink fw-bold">{props.displayId}</span>
         <Dropdown
            isOpen={open}
            toggle={() => setOpen(!open)}
            style={{ position: "absolute", top: "1.75em", left: "2em" }}
         >
            <DropdownMenu>
               {props.qualityAdmin &&
                  <DropdownItem className='cursor-pointer' onClick={() => goNavigate(props.navRoute)} >
                     View
                  </DropdownItem>
               }
               <DropdownItem
                  className='cursor-pointer'
                  onClick={() => props.viewReport(props.actionId, props.actionType, props.displayId)}
               >
                  Print
               </DropdownItem>
               <DropdownItem
                  onClick={() => navigate(`/attachments/${props.attachmentType}/${props.displayId}`)} // figure out what to pass to the attachmen table to see it's attachments
                  className="sort-handle"
               >
                  Attachments {props.attachmentCount > -1 ? `(${props.attachmentCount})` : null}
               </DropdownItem>
            </DropdownMenu>
         </Dropdown>
      </td >
   );
}